
import React, { useState, useEffect } from 'react';
import { Property, AgentProfile } from '../types';
import { X, MapPin, Bed, Bath, Move, Share2, MessageCircle, Phone, Heart, Check, ChevronRight, ChevronLeft, Edit2, Car, UtilityPole, Sofa, Settings2, Users, Trash2, AlertTriangle, EyeOff, Eye, ImageIcon } from 'lucide-react';
import { Logo } from './Logo';
import { ToastType } from './Toast';

interface PropertyDetailProps {
  property: Property;
  agent: AgentProfile;
  onClose: () => void;
  onAgentClick?: (agentId: string) => void;
  isOwner?: boolean;
  onEditClick?: (prop: Property) => void;
  onDeleteClick?: (propId: string) => void;
  onArchiveClick?: (id: string, archive: boolean) => void;
  onToast?: (message: string, type: ToastType) => void;
}

export const PropertyDetail: React.FC<PropertyDetailProps> = ({ property, agent, onClose, onAgentClick, isOwner, onEditClick, onDeleteClick, onArchiveClick, onToast }) => {
  const [activeImage, setActiveImage] = useState(0);
  const [copied, setCopied] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  useEffect(() => {
    const favorites = JSON.parse(localStorage.getItem('findily_favorites') || '[]');
    setIsFavorited(favorites.includes(property.id));
  }, [property.id]);

  const toggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    const favorites = JSON.parse(localStorage.getItem('findily_favorites') || '[]');
    let newFavorites;
    if (favorites.includes(property.id)) {
      newFavorites = favorites.filter((id: string) => id !== property.id);
      setIsFavorited(false);
    } else {
      newFavorites = [...favorites, property.id];
      setIsFavorited(true);
    }
    localStorage.setItem('findily_favorites', JSON.stringify(newFavorites));
  };

  const priceFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: property.currency || 'USD',
    maximumFractionDigits: 0,
  });

  const getPriceColorClass = (category: string) => {
    switch (category) {
      case 'Sale': return 'text-blue-600 dark:text-blue-400';
      case 'Rent': return 'text-green-600 dark:text-green-400';
      case 'Short Let': return 'text-orange-600 dark:text-orange-400';
      case 'JV': return 'text-purple-600 dark:text-purple-400';
      default: return 'text-gray-900 dark:text-white';
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        if (onToast) onToast("Listing link copied!", "success");
        setTimeout(() => setCopied(false), 2000);
      } else {
        throw new Error("No clipboard access");
      }
    } catch (err) {
      console.warn("Clipboard failed", err);
      if (onToast) onToast("Couldn't copy link.", "error");
    }
  };

  const handleShare = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // CLEAN REDIRECT DOMAIN: Direct to primary domain
    const shareUrl = `https://nas-ng.vercel.app/?prop=${property.id}`;
    
    const priceStr = priceFormatter.format(property.price);
    const shareText = `Check out this property on Findily: ${property.title} - ${priceStr}`;

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile && navigator.share) {
      navigator.share({
        title: property.title,
        text: shareText,
        url: shareUrl,
      }).catch((err) => {
        if (err.name !== 'AbortError') {
          copyToClipboard(shareUrl);
        }
      });
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const handleWhatsAppClick = () => {
    const shareUrl = `https://nas-ng.vercel.app/?prop=${property.id}`;
    const message = `Hi ${agent.business_name}, I am interested in your property: "${property.title}".\n\nView listing: ${shareUrl}\n\nPlease provide more details.`;
    window.open(`https://wa.me/${agent.whatsapp}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleCallClick = () => {
    const callNumber = agent.phone || agent.whatsapp;
    if (callNumber) {
      window.location.href = `tel:${callNumber}`;
    }
  };

  const handleAgentNav = () => {
    if (onAgentClick) {
      onAgentClick(agent.id);
      onClose();
    }
  };

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveImage(prev => (prev > 0 ? prev - 1 : property.images.length - 1));
  };

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveImage(prev => (prev < property.images.length - 1 ? prev + 1 : 0));
  };

  const DESCRIPTION_LIMIT = 180;
  const shouldShowDescriptionMore = property.description && property.description.length > DESCRIPTION_LIMIT;
  const displayedDescription = (shouldShowDescriptionMore && !isDescriptionExpanded)
    ? `${property.description.substring(0, DESCRIPTION_LIMIT)}...`
    : property.description;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/95 backdrop-blur-xl overflow-y-auto">
      
      <button 
        onClick={onClose} 
        className="absolute top-4 right-4 z-50 p-2.5 bg-black/40 hover:bg-black/60 backdrop-blur-md rounded-full text-white transition-all shadow-lg active:scale-90"
      >
        <X className="w-6 h-6 stroke-[2.5]" />
      </button>

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-white dark:bg-zinc-900 p-8 rounded-[32px] max-w-sm w-full text-center space-y-6 shadow-2xl animate-in zoom-in-95" onClick={e => e.stopPropagation()}>
            <div className="w-20 h-20 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto">
              <AlertTriangle className="w-10 h-10 text-red-500" />
            </div>
            <div>
              <h3 className="text-xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Delete Listing?</h3>
              <p className="text-sm text-gray-500 dark:text-zinc-500 font-medium mt-2">This cannot be undone. All data will be permanently removed.</p>
            </div>
            <div className="flex flex-col gap-3">
              <button 
                onClick={(e) => { e.stopPropagation(); onDeleteClick?.(property.id); }}
                className="w-full py-4 bg-red-500 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-red-600 transition-all active:scale-95"
              >
                Yes, Delete Permanently
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(false); }}
                className="w-full py-4 bg-gray-100 dark:bg-zinc-800 text-gray-900 dark:text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all active:scale-95"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-zinc-900 w-full max-w-6xl md:my-8 md:rounded-[32px] overflow-visible shadow-2xl relative flex flex-col md:flex-row transition-colors">
        
        <div className="w-full md:w-[60%] bg-black relative group/gallery select-none">
          <img 
            src={property.images[activeImage]} 
            alt={property.title} 
            className="w-full h-auto min-h-[300px] md:min-h-[500px] object-cover object-center block" 
            loading="lazy"
          />

          <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20 select-none">
            <div className="flex items-center gap-2 transform -rotate-12 border-2 border-white/40 px-6 py-2 rounded-2xl">
              <Logo size={40} />
              <span className="text-4xl font-black text-white tracking-tighter uppercase">Findily</span>
            </div>
          </div>
          
          {property.images.length > 1 && (
            <>
              <button onClick={prevImage} className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/40 hover:bg-black/60 backdrop-blur-md rounded-full text-white transition-all active:scale-90 shadow-2xl">
                <ChevronLeft className="w-6 h-6 stroke-[2.5]" />
              </button>
              <button onClick={nextImage} className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/40 hover:bg-black/60 backdrop-blur-md rounded-full text-white transition-all active:scale-90 shadow-2xl">
                <ChevronRight className="w-6 h-6 stroke-[2.5]" />
              </button>
            </>
          )}

          {/* Image Counter */}
          <div className="absolute top-4 right-4 z-10 px-3 py-1 bg-black/50 backdrop-blur-md rounded-full text-white text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 border border-white/10">
            <ImageIcon className="w-3 h-3" />
            {activeImage + 1} / {property.images.length}
          </div>

          <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-1.5 z-10">
            {property.images.map((_, i) => (
              <button 
                key={i} 
                onClick={(e) => { e.stopPropagation(); setActiveImage(i); }} 
                className={`w-1.5 h-1.5 rounded-full transition-all active:scale-95 ${i === activeImage ? 'bg-white scale-125 w-3' : 'bg-white/40 hover:bg-white/60'}`} 
              />
            ))}
          </div>
          
          <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
            <div className="flex gap-2">
              <span className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow-xl backdrop-blur-md ${
                property.market_status === 'Available' ? 'bg-green-500/90 text-white' : property.market_status === 'Sold' ? 'bg-red-500/90 text-white' : 'bg-amber-500/90 text-white'
              }`}>
                {property.market_status || property.status}
              </span>
              <span className="px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow-xl backdrop-blur-md bg-white/90 dark:bg-zinc-800/90 text-gray-900 dark:text-white">
                {property.category}
              </span>
            </div>
          </div>
        </div>

        <div className="w-full md:w-[40%] flex flex-col bg-white dark:bg-zinc-900 border-l border-gray-100 dark:border-zinc-800">
          
          {/* CONTROL STRIP FOR OWNER ACTIONS */}
          {isOwner && (
            <div className="flex items-center justify-between px-6 py-4 bg-gray-50 dark:bg-zinc-900/50 border-b border-gray-100 dark:border-zinc-800 animate-in fade-in slide-in-from-top-2 duration-300">
              <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Dashboard Control</span>
              <div className="flex items-center gap-2">
                <button 
                  onClick={(e) => { e.stopPropagation(); onEditClick?.(property); }} 
                  className="flex items-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-600 transition-all active:scale-95 shadow-md shadow-blue-500/10"
                >
                  <Edit2 className="w-3.5 h-3.5" /> Edit
                </button>
                {property.is_archived ? (
                  <button 
                    onClick={(e) => { e.stopPropagation(); onArchiveClick?.(property.id, false); }} 
                    className="flex items-center gap-2 px-3 py-2 bg-green-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-green-600 transition-all active:scale-95 shadow-md shadow-green-500/10"
                  >
                    <Eye className="w-3.5 h-3.5" /> Publish
                  </button>
                ) : (
                  <button 
                    onClick={(e) => { e.stopPropagation(); onArchiveClick?.(property.id, true); }} 
                    className="flex items-center gap-2 px-3 py-2 bg-amber-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-amber-600 transition-all active:scale-95 shadow-md shadow-amber-500/10"
                  >
                    <EyeOff className="w-3.5 h-3.5" /> Unpublish
                  </button>
                )}
                <button 
                  onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(true); }} 
                  className="flex items-center gap-2 px-3 py-2 bg-red-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-red-600 transition-all active:scale-95 shadow-md shadow-red-500/10"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 p-5 border-b border-gray-100 dark:border-zinc-800 cursor-pointer group" onClick={handleAgentNav}>
            <img src={agent.avatar} alt={agent.name} className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-100 dark:ring-zinc-800" loading="lazy" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-black text-gray-900 dark:text-white tracking-tight group-hover:underline truncate">{agent.business_name}</p>
                {agent.verification_status === 'verified' && <CheckCircle2Icon className="w-3.5 h-3.5 text-blue-500" />}
              </div>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {property.location}
              </p>
            </div>
            
            <button onClick={toggleFavorite} className={`p-2 rounded-lg transition-all active:scale-95 ${isFavorited ? 'bg-red-50 text-red-500' : 'bg-gray-100 dark:bg-zinc-800 text-gray-400 hover:text-red-500'}`}>
              <Heart className={`w-5 h-5 ${isFavorited ? 'fill-current' : ''}`} />
            </button>
          </div>

          <div className="flex-1 p-6 space-y-8 scrollbar-hide">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                 <h2 className="text-3xl font-black text-gray-900 dark:text-white leading-none tracking-tighter">{property.title}</h2>
                 <p className={`text-4xl font-black tracking-tighter ${getPriceColorClass(property.category)}`}>
                   {priceFormatter.format(property.price)}
                 </p>
                 {property.is_negotiable && (
                    <span className="inline-block px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-[10px] font-black uppercase rounded-lg">Negotiable</span>
                 )}
              </div>
              <button onClick={(e) => { e.stopPropagation(); handleShare(e); }} className={`flex flex-col items-center gap-1 p-3 rounded-2xl transition-all ${copied ? 'bg-green-50 text-green-600' : 'bg-gray-50 dark:bg-zinc-800 text-gray-500'}`}>
                {copied ? <Check className="w-6 h-6" /> : <Share2 className="w-6 h-6" />}
                <span className="text-[9px] font-black uppercase tracking-widest">{copied ? 'Copied' : 'Share'}</span>
              </button>
            </div>

            <div className="space-y-1 bg-gray-50 dark:bg-zinc-800/50 p-3 rounded-xl border border-gray-100 dark:border-zinc-800">
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Exact Location</p>
                <p className="text-sm font-bold text-gray-800 dark:text-white">
                  {property.street_estate ? `${property.street_estate}, ` : ''}{property.locality}
                </p>
            </div>

            <div className="grid grid-cols-3 gap-y-6 gap-x-2 py-6 border-y border-gray-50 dark:border-zinc-800">
              <div className="flex flex-col items-center gap-1 text-center">
                <Bed className="w-5 h-5 text-gray-400" />
                <span className="text-[9px] font-black uppercase tracking-widest text-gray-400">Beds</span>
                <span className="text-sm font-bold dark:text-white">{property.bedrooms || '—'}</span>
              </div>
              <div className="flex flex-col items-center gap-1 text-center">
                <Bath className="w-5 h-5 text-gray-400" />
                <span className="text-[9px] font-black uppercase tracking-widest text-gray-400">Baths</span>
                <span className="text-sm font-bold dark:text-white">{property.bathrooms || '—'}</span>
              </div>
              <div className="flex flex-col items-center gap-1 text-center">
                <UtilityPole className="w-5 h-5 text-gray-400" />
                <span className="text-[9px] font-black uppercase tracking-widest text-gray-400">Toilets</span>
                <span className="text-sm font-bold dark:text-white">{property.toilets || '—'}</span>
              </div>
              <div className="flex flex-col items-center gap-1 text-center">
                <Car className="w-5 h-5 text-gray-400" />
                <span className="text-[9px] font-black uppercase tracking-widest text-gray-400">Parking</span>
                <span className="text-sm font-bold dark:text-white">{property.parking || '—'}</span>
              </div>
              <div className="flex flex-col items-center gap-1 text-center">
                <Move className="w-5 h-5 text-gray-400" />
                <span className="text-[9px] font-black uppercase tracking-widest text-gray-400">Total Area</span>
                <span className="text-sm font-bold dark:text-white">{property.area} <span className="text-[10px]">sqm</span></span>
              </div>
              <div className="flex flex-col items-center gap-1 text-center">
                <Settings2 className="w-5 h-5 text-gray-400" />
                <span className="text-[9px] font-black uppercase tracking-widest text-gray-400">Covered</span>
                <span className="text-sm font-bold dark:text-white">{property.covered_area || '—'} <span className="text-[10px]">sqm</span></span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {property.is_furnished && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 dark:bg-green-900/10 border border-green-100 dark:border-green-900/20 rounded-lg text-green-600 dark:text-green-400 text-[10px] font-black uppercase tracking-widest">
                  <Sofa className="w-3.5 h-3.5" /> Fully Furnished
                </div>
              )}
              {property.is_serviced && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/20 rounded-lg text-blue-600 dark:text-blue-400 text-[10px] font-black uppercase tracking-widest">
                  <CheckCircle2Icon className="w-3.5 h-3.5" /> Serviced
                </div>
              )}
              {property.is_shared && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-900/20 rounded-lg text-purple-600 dark:text-purple-400 text-[10px] font-black uppercase tracking-widest">
                  <Users className="w-3.5 h-3.5" /> Shared Apartment
                </div>
              )}
            </div>

            {/* Amenities & Features Section */}
            {property.features && property.features.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Amenities & Features</h3>
                <div className="grid grid-cols-2 gap-y-3 gap-x-4">
                  {property.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center shrink-0">
                         <Check className="w-3 h-3 text-blue-500" />
                      </div>
                      <span className="text-xs font-bold text-gray-700 dark:text-zinc-400 truncate">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-4">
              <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Property Description</h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-sm font-medium whitespace-pre-wrap">
                {displayedDescription}
                {shouldShowDescriptionMore && (
                  <button 
                    onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                    className="ml-1 text-blue-500 dark:text-blue-400 font-black hover:underline focus:outline-none"
                  >
                    {isDescriptionExpanded ? 'less' : 'more'}
                  </button>
                )}
              </p>
            </div>
          </div>

          <div className="p-4 md:p-6 border-t border-gray-100 dark:border-zinc-800 bg-white/70 dark:bg-zinc-900/70 backdrop-blur-md sticky bottom-0 left-0 right-0 flex gap-3">
            <button onClick={(e) => { e.stopPropagation(); handleWhatsAppClick(); }} className="flex-[3] bg-green-600 text-white py-4 rounded-2xl font-black text-sm uppercase tracking-widest flex items-center justify-center gap-3 hover:bg-green-700 transition-all active:scale-95 shadow-xl shadow-green-500/20">
              <MessageCircle className="w-5 h-5 fill-current" /> WhatsApp {agent.business_name.split(' ')[0]}
            </button>
            <button onClick={(e) => { e.stopPropagation(); handleCallClick(); }} className="flex-1 bg-black dark:bg-white text-white dark:text-black py-4 rounded-2xl flex items-center justify-center hover:opacity-90 transition-all active:scale-95 shadow-lg">
              <Phone className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CheckCircle2Icon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
